import { Search, FileSignature, ShieldCheck } from "lucide-react";

const STEPS = [
  {
    num: "01",
    icon: Search,
    title: "Comprehensive Trademark Search",
    description:
      "The first and most critical step in the trademark registration process is conducting a thorough, comprehensive trademark search. Our team analyzes the USPTO's Trademark Electronic Search System (TESS), state trademark databases, common law sources, domain registries, and business directories to identify any existing marks that could potentially conflict with yours. We examine direct-hit matches, phonetically similar marks, visually similar designs, and conceptually related trademarks. This exhaustive search process allows us to assess the registrability of your proposed mark and provide you with a detailed risk analysis before proceeding with filing. If potential conflicts are identified, we work with you to develop alternative strategies that maximize your chances of successful registration while maintaining the integrity of your brand identity.",
  },
  {
    num: "02",
    icon: FileSignature,
    title: "Application Preparation & Filing",
    description:
      "Once your trademark search confirms that your proposed mark is available for registration, our team moves forward with preparing and filing your application with the USPTO. This stage involves selecting the correct filing basis (use in commerce or intent to use), accurately classifying your goods and services under the international Nice Classification system, drafting a clear and comprehensive description of the mark, and preparing all required specimens and supporting documentation. Our experienced professionals ensure that every element of your application is precisely prepared to meet USPTO standards, significantly reducing the likelihood of receiving an office action or refusal. We file your application electronically through the USPTO's Trademark Electronic Application System (TEAS) and provide you with immediate confirmation of submission along with your serial number for tracking purposes.",
  },
  {
    num: "03",
    icon: ShieldCheck,
    title: "Approval, Registration & Monitoring",
    description:
      "After your application is filed, it undergoes examination by a USPTO trademark examining attorney, followed by publication in the Official Gazette for opposition. Our team monitors your application through every stage of this process, responding promptly to any office actions or third-party oppositions that may arise. Once your trademark is approved and no oppositions are filed, you receive your official Certificate of Registration from the USPTO, granting you exclusive nationwide rights to use your mark in connection with the registered goods and services. But our service does not stop there. We offer ongoing trademark monitoring to watch for new applications that may conflict with your mark, timely reminders for maintenance filings required at the 5th and 10th year anniversaries, and continued support to help you enforce and protect your valuable trademark rights for years to come.",
  },
];

export default function ProcessSection() {
  return (
    <section id="process" data-testid="process-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A8A] mb-4">
            Our Process
          </span>
          <h2
            data-testid="process-heading"
            className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] tracking-tight mb-4"
          >
            Trademark Registration in 3 Simple Steps
          </h2>
          <p className="text-[#475569] text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
            We have simplified the complex USPTO trademark registration process into three straightforward steps. Our streamlined approach ensures your brand receives federal protection efficiently and effectively, with expert guidance at every stage.
          </p>
        </div>

        <div className="relative">
          {/* Connection line (desktop) */}
          <div className="hidden lg:block absolute top-24 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-[#1E3A8A] via-[#2563EB] to-[#1E3A8A]" />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {STEPS.map((step, i) => (
              <div key={step.num} data-testid={`process-step-${i}`} className="relative">
                <div className="bg-[#F8F9FA] border border-slate-200 rounded-lg p-8 hover:shadow-lg transition-all duration-300 h-full">
                  {/* Step number */}
                  <div className="relative z-10 w-14 h-14 rounded-full bg-[#1E3A8A] flex items-center justify-center mb-6 mx-auto lg:mx-0 shadow-lg shadow-blue-900/20">
                    <span className="font-heading text-lg font-extrabold text-white">{step.num}</span>
                  </div>

                  <h3 className="font-heading text-xl font-semibold text-[#0F172A] mb-4 text-center lg:text-left">
                    {step.title}
                  </h3>
                  <p className="text-[#475569] text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
